/**
 * Formatting and colouring rules of the Strategic KPIs page. Pure functions, unit-tested.
 *
 * The traffic light follows the three Power BI reports until the review with Elena (P5)
 * decides otherwise: ≥ 100 % green, ≥ 90 % yellow, below red. It reads the engine's
 * `achievement`, which is ALREADY inverted for lower-is-better KPIs (Avg Days: target /
 * actual) — so the light never has to know the direction, and a second inversion here would
 * turn a good value red.
 */
import type { DateWindow, KpiCatalogueEntry } from '../types/strategicKpis';
import { formatDate } from './format';

export type TrafficLight = 'green' | 'yellow' | 'red';

export const GREEN_FROM = 1.0;
export const YELLOW_FROM = 0.9;

/** null = no light (no target, or no value) — rendered neutral, never red. */
export function trafficLight(achievement: number | null | undefined): TrafficLight | null {
    if (achievement === null || achievement === undefined || !Number.isFinite(achievement)) return null;
    if (achievement >= GREEN_FROM) return 'green';
    if (achievement >= YELLOW_FROM) return 'yellow';
    return 'red';
}

/**
 * K5 (ND-01, design.md §2 and §8): the status text is neutral — only its dot carries the light.
 * Since the redesign (masterplan skpi-redesign, B1, rule 3) both are classes of the scoped kit
 * (`styles/skpi/kit.css`), never stock Tailwind colours: the text is `muted`; the dot is `.dot` in
 * the kit's variance tints — `fav` (teal) on track, `unfav` (orange) behind, amber (`attn`) for close (E23),
 * hollow for no light. The word `STATUS_LABEL` always sits beside it.
 */
export const LIGHT_CLASS: Record<TrafficLight, string> = {
    green: 'muted',
    yellow: 'muted',
    red: 'muted',
};

export const LIGHT_DOT_CLASS: Record<TrafficLight, string> = {
    green: 'dot fav',
    yellow: 'dot attn',
    red: 'dot unfav',
};

/** A light or its absence — 'none' is "no target" or "no pace" (achievement null), never red. */
export type Light = TrafficLight | 'none';

/** The word next to the colour — a status is never colour alone (WCAG 1.4.1). */
export const STATUS_LABEL: Record<Light, string> = {
    green: 'on track',
    yellow: 'close',
    red: 'behind',
    none: 'no pace',
};

export const BADGE_CLASS: Record<Light, string> = { ...LIGHT_CLASS, none: 'muted' };
export const DOT_CLASS: Record<Light, string> = { ...LIGHT_DOT_CLASS, none: 'dot none' };

/**
 * Whether the achievement is judged against the YTD share of the target (`targetYtd`): the engine
 * says so per KPI (visits; the GP amounts from version 2, plan answer 21). A response without the
 * flag is read as before: visits only.
 */
export function judgedOnYtdShare(kpi: Pick<KpiCatalogueEntry, 'category' | 'targetProrated'>): boolean {
    return kpi.targetProrated ?? kpi.category === 'visits';
}

/**
 * What the achievement is judged against, as a short label next to the target. Visits and the
 * GP amounts (answer 21) are prorated by the engine (targetYtd); a rolling 12-month window is a
 * full year of data and compares with the year target directly; a status compares with the year
 * target; the remaining YTD families have no proration in the engine — a stored target would
 * compare against the full year (said so, never hidden).
 */
export function paceBasisText(kpi: Pick<KpiCatalogueEntry, 'category' | 'windowType' | 'targetProrated'>): string {
    if (judgedOnYtdShare(kpi)) return 'vs YTD share';
    if (kpi.windowType === 'R12') return 'rolling 12 m vs year target';
    if (kpi.windowType === 'POINT') return 'status vs year target';
    return 'vs full-year target';
}

export interface PriorDirection {
    arrow: '▲' | '▼' | '•';
    /** null = flat; for lower-is-better KPIs a fall is favourable. */
    favourable: boolean | null;
}

/** Direction of the actual against the prior year — a comparison shown as an arrow, never a number. */
export function priorDirection(kpi: Pick<KpiCatalogueEntry, 'direction'>, actual: number | null | undefined, prior: number | null | undefined): PriorDirection | null {
    if (actual === null || actual === undefined || prior === null || prior === undefined || !Number.isFinite(actual) || !Number.isFinite(prior)) return null;
    if (actual === prior) return { arrow: '•', favourable: null };
    const up = actual > prior;
    return { arrow: up ? '▲' : '▼', favourable: kpi.direction === 'lower' ? !up : up };
}

/** K5 (ND-01, S1-06): the judgement next to the neutral arrow, as a word — up is not "good" for Avg Days. */
export function priorWord(dir: PriorDirection): 'better' | 'worse' | 'same' {
    return dir.favourable === null ? 'same' : dir.favourable ? 'better' : 'worse';
}

/** K5 (S2-02, design.md §3): an amount names its unit where the reader sees it — "Open GP (EUR)". */
export function kpiLabel(kpi: { name: string; unit: string } | { Name: string; Unit: string }): string {
    const [name, unit] = 'name' in kpi ? [kpi.name, kpi.unit] : [kpi.Name, kpi.Unit];
    return unit === 'amount' ? `${name} (EUR)` : name;
}

const CH = 'de-CH';

/**
 * A KPI value in its unit. `pct` arrives as a fraction (0.3 = 30 %), amounts are EUR without a
 * symbol — the unit sits once in the header or label (`kpiLabel`, K5), not in every cell — days
 * with one decimal, counts as integers.
 */
export function formatKpiValue(unit: string, value: number | null | undefined): string {
    if (value === null || value === undefined || !Number.isFinite(value)) return '—';
    switch (unit) {
        case 'pct': return `${(value * 100).toLocaleString(CH, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;
        case 'amount': return value.toLocaleString(CH, { maximumFractionDigits: 0 });
        case 'days': return value.toLocaleString(CH, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
        default: return value.toLocaleString(CH, { maximumFractionDigits: value % 1 === 0 ? 0 : 1 });
    }
}

/**
 * Achievement as a percentage. "n/a" is reserved for "no target": an actual of 0 against a
 * target is 0 %, not n/a (masterplan P3 acceptance).
 */
export function formatAchievement(achievement: number | null | undefined, hasTarget: boolean): string {
    if (!hasTarget) return 'n/a';
    if (achievement === null || achievement === undefined || !Number.isFinite(achievement)) return '—';
    return `${Math.round(achievement * 100).toLocaleString(CH)} %`;
}

export const UNIT_LABEL: Record<string, string> = {
    count: 'count',
    amount: 'EUR',
    pct: '%',
    days: 'days',
};

export const CATEGORY_LABEL: Record<string, string> = {
    crm: 'CRM',
    visits: 'Visits',
    gp: 'Gross Profit',
    bab: 'BAB',
    marketdata: 'Market Data',
};

export const CATEGORY_ORDER = ['crm', 'visits', 'gp', 'bab', 'marketdata'];

export const AGGREGATION_LABEL: Record<string, string> = {
    sum: 'sum of the persons',
    pooled: 'pooled (Σ numerator ÷ Σ denominator)',
    avg: 'average of the persons',
    mean: 'mean of the persons',
    derived: 'derived from other KPIs',
};

/** The engine's German noData reasons, rendered in English; unknown texts pass through. */
const NO_DATA_EN: Record<string, string> = {
    'Person hat keine HubSpot-Owner-Id': 'Person has no HubSpot owner id',
    'Person hat keinen etl.SalesManager-Code': 'Person has no sales-manager code in the ERP',
    'keine Umsatzzeilen im Fenster': 'No sales lines in the window',
    'STAN-Zuordnung liegt in einer SharePoint-Excel, die die App nicht liest (Review-Frage 11)': 'STAN allocation lives in a SharePoint Excel the app does not read (review question 11)',
    'BAB hat keine Personen-Ebene (nur Company/BU)': 'BAB has no person level (company / BU only)',
    'kein BAB-Freeze für diesen Monat': 'No BAB freeze for this month',
    'Status des Vorjahres ist aus der Replica nicht rekonstruierbar': 'Last year\'s status cannot be reconstructed from the replica',
    'keine Daten in diesem Bereich': 'No data in this scope',
    'kein Besuchs-Target': 'No visit target',
};

export function noDataText(reason: string | undefined): string {
    if (!reason) return 'No data';
    return NO_DATA_EN[reason] ?? reason;
}

/** Bitmask of KpiDefinition.TargetGrain → the scopes a target may be stored at. */
export function targetGrainText(grain: number): string {
    const parts: string[] = [];
    if (grain & 1) parts.push('Person');
    if (grain & 2) parts.push('Team');
    if (grain & 4) parts.push('Company');
    if (grain & 8) parts.push('BU');
    return parts.length === 0 ? 'no stored target (derived)' : parts.join(', ');
}

/**
 * "Target stored at" of the Definitions tab (K5, S2-10): a KPI without a target grain is not
 * always derived — C visits are calculated from the Total, Average % is derived, Success Rate and
 * Avg Days are context KPIs that take no target since definition version 4.
 */
export function targetStoredText(d: { TargetGrain: number; GroupAggregation: string; DerivedTarget?: string | null }): string {
    if (d.TargetGrain !== 0) return targetGrainText(d.TargetGrain);
    if (d.DerivedTarget) return `calculated by the server as ${d.DerivedTarget}`;
    if (d.GroupAggregation === 'derived' || d.GroupAggregation === 'mean') return 'no stored target (derived)';
    return 'no target (context KPI)';
}

/**
 * The window of a KPI as English text, from the engine's structured `from` / `to` — the
 * engine's own `text` is German and stays available in the tooltip. Same dates, same rule:
 * R12 = rolling twelve months to the as-of date, YTD = 1 January to the month end of the
 * as-of date, POINT = the replica's status on the as-of date, BAB = the freeze of that month.
 */
export function windowText(kpi: Pick<KpiCatalogueEntry, 'category' | 'windowType'>, w: DateWindow): string {
    if (kpi.category === 'bab') {
        const to = w.to.slice(0, 10);
        return `BAB freeze ${to.slice(5, 7)}/${to.slice(0, 4)} (YTD)`;
    }
    switch (kpi.windowType) {
        case 'R12': return w.from ? `Rolling 12 months, ${formatDate(w.from)} – ${formatDate(w.to)}` : `Rolling 12 months to ${formatDate(w.to)}`;
        case 'POINT': return w.from === null && /kein Vorjahreswert/.test(w.text) ? 'No prior-year value (status)' : `As of ${formatDate(w.to)}`;
        default: return `YTD to ${formatDate(w.to)}`;
    }
}

export const WINDOW_TYPE_LABEL: Record<string, string> = {
    R12: 'rolling 12 months to the as-of date',
    YTD: 'year to date (visits: to the as-of date since version 2; GP, BAB, market data: to the month end of the as-of date)',
    POINT: 'status on the as-of date',
    YEAR: 'calendar year',
};

// ---------------------------------------------------------------------------------------------
// Targets tab (P4): the text in an edit box <-> the stored number
// ---------------------------------------------------------------------------------------------

/**
 * The stored value as the text a person edits: pct as a PERCENTAGE (0.3 -> "30"), everything
 * else as the plain number — no thousands separators, dot as decimal point, so the text can go
 * straight back through parseTargetInput.
 */
export function targetInputText(unit: string, value: number | null | undefined): string {
    if (value === null || value === undefined || !Number.isFinite(value)) return '';
    const n = unit === 'pct' ? value * 100 : value;
    return String(Number(n.toFixed(unit === 'pct' ? 2 : 4)));
}

export type ParsedTargetInput = { ok: true; value: number | null } | { ok: false; error: string };

/**
 * Edit-box text -> value for PUT. Empty = remove the target (null). Accepts Swiss and English
 * number writing (1'234.5 / 1 234,5 / 1234.5); pct is typed as a percentage and stored as a
 * fraction (30 -> 0.3), so "0.3 %" is not a way to say 30 %. Refuses anything else — a value
 * that cannot be read must not silently become 0 (P1 rule: numbers only, never strings).
 */
export function parseTargetInput(unit: string, text: string): ParsedTargetInput {
    const raw = text.trim();
    if (raw === '') return { ok: true, value: null };
    let s = raw.replace(/\s*%$/, '').replace(/[\s'\u2019_]/g, '');
    // "1.234,5" (comma decimal with dot thousands) or "1234,5" -> "1234.5"
    if (s.includes(',') && !s.includes('.')) s = s.replace(',', '.');
    else if (s.includes(',') && s.includes('.')) s = s.replace(/\./g, '').replace(',', '.');
    if (!/^\d+(\.\d+)?$/.test(s)) return { ok: false, error: `"${raw}" is not a number` };
    const n = Number(s);
    if (!Number.isFinite(n)) return { ok: false, error: `"${raw}" is not a number` };
    if (unit === 'pct') {
        if (n > 100) return { ok: false, error: 'a percentage target must be between 0 and 100' };
        return { ok: true, value: Number((n / 100).toFixed(4)) };
    }
    return { ok: true, value: Number(n.toFixed(4)) };
}

export const SCOPE_TYPE_LABEL: Record<string, string> = {
    PERSON: 'Person', TEAM: 'Team', COMPANY: 'Company', BU: 'Business unit',
};

export const SOURCE_LABEL: Record<string, string> = {
    // 'migration' covers the seed from etl.CrmTargets (187) and the K2 switch (218 / 219); the note says which.
    ui: 'Edited on the page', migration: 'Migration', rollover: 'Rollover',
};
